import { useEffect, useState } from 'react';
import { Button, Col, Space, Table, Tag, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { DownloadOutlined } from '@ant-design/icons';
import { SubscriptionService } from '../../Services/SubscriptionService';
import { TextService } from '../../Services/TextService';
import { formatToShortFormat } from '../../Helpers/DateHelpers';
import { InvoiceStatus } from '../../Types/InvoiceStatus';
import { WebApiServicesDataTransferObjectsSubscriptionServiceCompanyInvoiceListItemDto } from '../../Repository/eventbookyapi';

type CompanyInvoice = WebApiServicesDataTransferObjectsSubscriptionServiceCompanyInvoiceListItemDto;

/**
 * Invoices OneFront has sent to the company. Only rendered for GlobalAdmin; hidden when there are none.
 */
const CompanyInvoices = () => {
    const [invoices, setInvoices] = useState<CompanyInvoice[]>([]);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const GetText = TextService.GetText;

    useEffect(() => {
        SubscriptionService.GetCompanyInvoices()
            .then(setInvoices)
            .catch(() => setInvoices([]));
    }, []);

    const download = async (invoice: CompanyInvoice) => {
        setDownloadingId(invoice.id!);
        try {
            await SubscriptionService.DownloadCompanyInvoicePdf(invoice.id!, invoice.invoiceNumber);
        } catch {
            message.error(GetText("Payment_InvoiceDownloadError"));
        } finally {
            setDownloadingId(null);
        }
    };

    const columns: ColumnsType<CompanyInvoice> = [
        {
            title: GetText("Payment_InvoiceNumber"),
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
        },
        {
            title: GetText("Payment_InvoiceDate"),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (_, invoice) => formatToShortFormat(invoice.createdAt),
        },
        {
            title: GetText("Payment_InvoiceStatus"),
            dataIndex: 'status',
            key: 'status',
            render: (_, invoice) => Number(invoice.status) === InvoiceStatus.Paid
                ? <Tag color="green">{GetText("Payment_InvoiceStatusPaid")}</Tag>
                : <Tag color="orange">{GetText("Payment_InvoiceStatusNew")}</Tag>,
        },
        {
            title: '',
            key: 'download',
            render: (_, invoice) => invoice.hasPdf ?
                <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={downloadingId === invoice.id}
                    onClick={() => download(invoice)}
                    data-testid={`company-invoice-download-${invoice.id}`}
                >
                    {GetText("Payment_InvoiceDownload")}
                </Button> : null,
        },
    ];

    if (invoices.length === 0) {
        return null;
    }

    return <Col className='bookyBox' style={{ marginLeft: "20px", marginTop: "20px", marginBottom: "20px" }} span={24}>
        <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
            <h3>{GetText("Payment_InvoicesTitle")}</h3>
        </Space>
        <Table
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={invoices}
            pagination={invoices.length > 10 ? { pageSize: 10 } : false}
            data-testid="company-invoices-table"
        />
    </Col>;
};

export default CompanyInvoices;
